import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input, output } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { s25Resource } from "../../../util/s25.resource";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { ProgressNode, ProgressResponse, TrailApi, TrailProgress } from "./trail.api";
import { TrailLabels } from "./trail.labels";
import { progressStatusLabel } from "./trail.progress.display";
import { TrailProgressTreeComponent } from "./trail.progress.tree.component";

export interface TrailSubmitExperience {
    nodeId: number;
    defnId?: number;
    trailId?: number;
}

function progressTreeHasContent(tree: ProgressNode | undefined): boolean {
    if (!tree) return false;
    if (tree.children?.length) return true;
    return tree.nodeType === "requirement";
}

@TypeManagerDecorator("s25-ng-trail-progress")
@Component({
    selector: "s25-ng-trail-progress",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, S25LoadingInlineStaticComponent, TrailProgressTreeComponent],
    template: `
        <div class="trail-progress">
            @if (progressRes.isLoading()) {
                <s25-ng-loading-inline-static [text]="'Loading progress…'" />
            } @else if (progressRes.error()) {
                <p class="trail-progress__error">Unable to load progress.</p>
            } @else if (progressData()) {
                <header class="trail-progress__header">
                    <h3 class="trail-progress__title">{{ progressData()!.displayName }}</h3>
                    @if (progressData()!.endingAwardDisplayName) {
                        <span class="trail-progress__award">{{ progressData()!.endingAwardDisplayName }}</span>
                    }
                    @if (progressData()!.defnEarned) {
                        <span class="trail-progress__earned-badge">{{ labels.AWARD_EARNED }}</span>
                    }
                </header>
                @if (isGrandfatheredDefn()) {
                    <div class="trail-progress__grandfathered-banner">
                        {{ labels.GRANDFATHERED_DEFN }}
                    </div>
                }

                <section class="trail-progress__curric-tree" [attr.aria-label]="labels.ENDING_AWARD">
                    <h4 class="trail-progress__section-title">{{ labels.ENDING_AWARD }}</h4>
                    @if (hasAwardTree()) {
                        <div class="trail-progress__tree">
                            <s25-ng-trail-progress-tree
                                [node]="progressData()!.curriculumTree"
                                [showSubmitExperience]="true"
                                (submitExperience)="onSubmitExperience($event)"
                            />
                        </div>
                    } @else {
                        <p class="trail-progress__empty" role="status">{{ labels.NO_AWARDS }}</p>
                    }
                </section>

                @if (progressData()!.trackTrees?.length) {
                    <section class="trail-progress__track-trees" [attr.aria-label]="labels.TRACKS">
                        <h4 class="trail-progress__section-title">{{ labels.TRACKS }}</h4>
                        <ul class="trail-progress__trail-list">
                            @for (track of progressData()!.trackTrees; track track.trackId) {
                                <li class="trail-progress__trail" [attr.data-id]="'track-tree-' + track.trackId">
                                    <div class="trail-progress__trail-header">
                                        <span class="trail-progress__trail-name">{{ track.name }}</span>
                                        @if (track.trackEarned) {
                                            <span class="trail-progress__summit-indicator">{{
                                                labels.TRACK_EARNED
                                            }}</span>
                                        }
                                        @if (track.trackEarned && track.tree?.status !== "satisfied") {
                                            <span class="trail-progress__grandfathered-hint">{{
                                                labels.GRANDFATHERED_TRACK
                                            }}</span>
                                        }
                                    </div>
                                    @if (track.cannotCombineWith?.length) {
                                        <p class="trail-progress__track-exclusivity">
                                            {{ labels.CANNOT_COMBINE_WITH }}: {{ track.cannotCombineWith.join(", ") }}
                                        </p>
                                    }
                                    <div class="trail-progress__tree">
                                        <s25-ng-trail-progress-tree
                                            [node]="track.tree"
                                            [showSubmitExperience]="true"
                                            (submitExperience)="onSubmitExperience($event)"
                                        />
                                    </div>
                                </li>
                            }
                        </ul>
                    </section>
                }

                <section class="trail-progress__trails" [attr.aria-label]="labels.TRAILS">
                    <h4 class="trail-progress__section-title">{{ labels.TRAILS }}</h4>
                    @if (sortedTrails().length) {
                        <ul class="trail-progress__trail-list">
                            @for (trail of sortedTrails(); track trail.trailId) {
                                <li class="trail-progress__trail">
                                    <div class="trail-progress__trail-header">
                                        <span class="trail-progress__trail-name">{{ trail.name }}</span>
                                        @if (trail.focusAreaName) {
                                            <span class="trail-progress__focus-badge">{{ trail.focusAreaName }}</span>
                                        }
                                        <span
                                            class="trail-progress__status-badge trail-progress__status-badge--{{
                                                trail.status
                                            }}"
                                        >
                                            {{ progressStatusLabel(trail.status) }}
                                        </span>
                                        @if (trail.summitEarned) {
                                            <span class="trail-progress__summit-indicator">{{
                                                labels.SUMMIT_EARNED
                                            }}</span>
                                        }
                                        @if (trail.summitEarned && trail.status !== "satisfied") {
                                            <span class="trail-progress__grandfathered-hint">{{
                                                labels.GRANDFATHERED_SUMMIT
                                            }}</span>
                                        }
                                        @if (trail.status === "locked") {
                                            <span class="trail-progress__lock-icon" title="Prerequisites required"
                                                >🔒</span
                                            >
                                        }
                                    </div>
                                    <div class="trail-progress__tree">
                                        <s25-ng-trail-progress-tree
                                            [node]="trail.tree"
                                            [showSubmitExperience]="true"
                                            (submitExperience)="onSubmitExperience($event)"
                                        />
                                    </div>
                                </li>
                            }
                        </ul>
                    } @else {
                        <p class="trail-progress__empty" role="status">{{ labels.NO_TRAILS }}</p>
                    }
                </section>
            }
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }
            .trail-progress__section-title {
                margin: 1rem 0 0.5rem;
                font-size: 1rem;
            }
            .trail-progress__empty {
                margin: 0;
                color: var(--color--text-muted);
                font-style: italic;
            }
            .trail-progress__curric-tree {
                margin-bottom: 1rem;
            }
            .trail-progress__track-exclusivity {
                margin: 0.25rem 0 0;
                font-size: 0.82rem;
                color: var(--color--text-muted, #555);
            }
            .trail-progress__header {
                margin-bottom: 1rem;
                display: flex;
                align-items: center;
                gap: 0.75rem;
                flex-wrap: wrap;
            }
            .trail-progress__title {
                margin: 0;
                font-size: 1.15rem;
            }
            .trail-progress__award {
                font-size: 0.85rem;
                color: var(--color--text-muted);
            }
            .trail-progress__earned-badge {
                background: var(--cn-navy, #1f4486);
                color: var(--color--primary-fg, #fff);
                padding: 0.2rem 0.6rem;
                border-radius: 4px;
                font-size: 0.8rem;
                font-weight: 600;
            }
            .trail-progress__error {
                color: var(--color--danger);
            }
            .trail-progress__grandfathered-banner {
                background: #fff3cd;
                border: 1px solid #ffc107;
                border-radius: 6px;
                padding: 0.5rem 0.75rem;
                margin-bottom: 1rem;
                font-size: 0.88rem;
                color: #856404;
            }
            .trail-progress__grandfathered-hint {
                font-size: 0.75rem;
                color: var(--color--text-muted);
                font-style: italic;
            }
            .trail-progress__trail-list {
                list-style: none;
                margin: 0;
                padding: 0;
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }
            .trail-progress__trail {
                border: 1px solid var(--color--border);
                border-radius: 8px;
                padding: 0.75rem 1rem;
                background: var(--color--surface-2-bg);
            }
            .trail-progress__trail-header {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                flex-wrap: wrap;
                margin-bottom: 0.5rem;
            }
            .trail-progress__trail-name {
                font-weight: 600;
                font-size: 0.95rem;
            }
            .trail-progress__focus-badge {
                background: var(--cn-navy-100, #e8eaf0);
                padding: 0.15rem 0.5rem;
                border-radius: 4px;
                font-size: 0.78rem;
                font-weight: 500;
            }
            .trail-progress__status-badge {
                padding: 0.15rem 0.45rem;
                border-radius: 4px;
                font-size: 0.75rem;
                font-weight: 600;
            }
            .trail-progress__status-badge--satisfied {
                background: #d4edda;
                color: #155724;
            }
            .trail-progress__status-badge--in_progress {
                background: #fff3cd;
                color: #856404;
            }
            .trail-progress__status-badge--not_started {
                background: #e2e3e5;
                color: #383d41;
            }
            .trail-progress__status-badge--not_yet_open {
                background: #d6d8db;
                color: #383d41;
            }
            .trail-progress__status-badge--expired {
                background: #f8d7da;
                color: #721c24;
            }
            .trail-progress__status-badge--locked {
                background: #d6d8db;
                color: #383d41;
            }
            .trail-progress__summit-indicator {
                font-size: 0.78rem;
                color: var(--cn-navy, #1f4486);
                font-weight: 600;
            }
            .trail-progress__lock-icon {
                font-size: 0.9rem;
            }
            .trail-progress__tree {
                margin-top: 0.25rem;
            }
        `,
    ],
})
export class TrailProgressComponent {
    readonly defnId = input.required<number>();
    readonly reloadToken = input(0);
    readonly submitExperience = output<TrailSubmitExperience>();
    readonly labels = TrailLabels;
    readonly progressStatusLabel = progressStatusLabel;

    readonly progressRes = s25Resource({
        params: () => ({ defnId: this.defnId(), reloadToken: this.reloadToken() }),
        loader: ({ params }) => TrailApi.getProgress(params.defnId),
    });

    readonly progressData = computed((): ProgressResponse | undefined => this.progressRes.value());

    readonly isGrandfatheredDefn = computed(() => {
        const data = this.progressData();
        if (!data?.defnEarned) return false;
        if (data.curriculumSatisfied != null) return !data.curriculumSatisfied;
        return data.curriculumTree?.status !== "satisfied";
    });

    readonly sortedTrails = computed((): TrailProgress[] => {
        const data = this.progressData();
        if (!data?.trails) return [];
        return [...data.trails].sort((trailA, trailB) => {
            if (trailA.topoLevel !== trailB.topoLevel) return trailA.topoLevel - trailB.topoLevel;
            const sortA = trailA.catalogSortOrder ?? Number.MAX_SAFE_INTEGER;
            const sortB = trailB.catalogSortOrder ?? Number.MAX_SAFE_INTEGER;
            if (sortA !== sortB) return sortA - sortB;
            return (trailA.name ?? "").localeCompare(trailB.name ?? "");
        });
    });

    readonly hasAwardTree = computed(() => progressTreeHasContent(this.progressData()?.curriculumTree));

    onSubmitExperience(node: ProgressNode): void {
        const progressData = this.progressData();
        const currentTrail = this.sortedTrails().find((trail) => this.treeContainsNode(trail.tree, node.nodeId));
        this.submitExperience.emit({
            nodeId: node.nodeId,
            defnId: progressData?.defnId,
            trailId: currentTrail?.trailId,
        });
    }

    private treeContainsNode(tree: ProgressNode, nodeId: number): boolean {
        if (tree.nodeId === nodeId) return true;
        return tree.children?.some((child) => this.treeContainsNode(child, nodeId)) ?? false;
    }
}
